import React, {useState} from 'react';
import {Image, Pressable, ScrollView, StyleSheet, View} from 'react-native';
import CCText from '@/Atoms/CCText';
import Box from '@/Atoms/Box';
import CCLoader from '@/Atoms/CCLoader';
import AnimatedBar from '@/Atoms/AnimatedBar';
import CCColors from '@/Utils/CCColors';
import {useGetPlayerGamesApi} from '@/Hooks/PlayerProfileApi/useGetPlayerGamesApi';
import ProfileAsset from '../../ui/ProfileAsset';
// Compact square platform glyphs: Lichess's knight and the CircleChess icon
// already ship in the app; the square Chess.com mark is a small in-house glyph
// (generic pawn on the platform's green) since only a wide wordmark existed.
import lichessLogo from '@/Assets/Images/Svgs/lichess_knight.svg';
import chesscomLogo from '@/Assets/Images/Svgs/chesscom-mark.svg';
import ccLogo from '@/Assets/Images/PlayerProfile/cc-icon.svg';

// Open another player's profile. `push` (not `navigate`) so nested profiles
// become real stack entries and the back button steps back one at a time.
const openProfile = (navigation: any, username?: string | null) => {
  if (!username) {
    return;
  }
  if (navigation?.push) {
    navigation.push('PlayerProfile', {username});
  } else {
    navigation?.navigate?.('PlayerProfile', {username});
  }
};

// Platform logo shown before the opponent name (design's Games table). Actual
// brand logos read faster than "CC"/"Li"/"Ch" letters.
//
// The stored `platform` string is inconsistent across imports -- CircleChess
// games appear as "Circlechess"/"CircleChess"/"cc"/"official", Chess.com as
// "chess.com"/"Chess.com", Lichess as "lichess"/"Lichess", plus over-the-board
// "OTB". A raw lower-cased lookup against fixed keys silently dropped the mark
// for anything that didn't match exactly (most visibly Chess.com: "chess.com"
// never equals a "chess_com" key), so normalise to letters-only first.
//
// All three glyphs are square, rendered at a common 18px in a fixed-width slot
// (styles.platLogoSlot) so opponent names stay left-aligned regardless of which
// glyph (or none) precedes them.
const PLAT_GLYPH = 18;
const platformLogo = (
  platform?: string | null,
): {logo: any; name: string} | undefined => {
  if (!platform) {
    return undefined;
  }
  const key = platform.toLowerCase().replace(/[^a-z]/g, '');
  if (key === 'lichess') {
    return {logo: lichessLogo, name: 'Lichess'};
  }
  if (key === 'chesscom') {
    return {logo: chesscomLogo, name: 'Chess.com'};
  }
  if (key === 'circlechess' || key === 'cc' || key === 'official') {
    return {logo: ccLogo, name: 'CircleChess'};
  }
  // OTB / unknown -- no online-platform glyph.
  return undefined;
};

const styles = StyleSheet.create({
  // Spec §2 card shell: radius 20, padding 24/26, soft shadow, no border.
  card: {
    backgroundColor: '#fff',
    borderRadius: 20,
    paddingVertical: 24,
    paddingHorizontal: 26,
    marginBottom: 18,
    shadowColor: 'rgba(77,63,55,1)',
    shadowOffset: {width: 0, height: 2},
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 2,
  },
  filterRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 16,
  },
  filterChip: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 999,
    backgroundColor: '#FAF6EB',
  },
  filterChipActive: {
    backgroundColor: '#4D3F37',
  },
  filterChipText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#4D3F37',
  },
  filterChipTextActive: {
    color: '#fff',
  },
  // Sum of every column's own minWidth (colResult 50 + colFormat 60 +
  // colMoves 40 + colAccuracy 90 + colOpening 80 + colRating 74 +
  // colActions 116 + headerRow's own gaps), plus room for colOpponent's
  // content -- the width the table needs to render without squeezing any
  // column, inside the horizontal ScrollView above.
  tableMinWidth: {
    minWidth: 680,
  },
  // Table header (uppercase micro labels), matching the design's Games table.
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingBottom: 10,
    gap: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F0EDE7',
  },
  headerCell: {
    fontSize: 11,
    fontWeight: '600',
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    color: '#888280',
    textAlign: 'center',
  },
  gameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F0EDE7',
  },
  // Column widths shared by the header and every row so they stay aligned.
  // Ratios from DEV_HANDOFF_SPEC §Grid: 50px 1.4fr .9fr .5fr .9fr .8fr 74px 116px.
  // Every column is centered (header label + cell content).
  colResult: {width: 50, alignItems: 'center'},
  colOpponent: {flex: 1.4, minWidth: 0},
  colFormat: {flex: 0.9, minWidth: 60},
  colMoves: {flex: 0.5, minWidth: 40},
  colAccuracy: {flex: 0.9, minWidth: 90},
  colOpening: {flex: 0.8, minWidth: 80},
  colRating: {width: 74, textAlign: 'center'},
  colActions: {width: 116},
  actionsCell: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  opponentNameRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: 6,
  },
  opponentRating: {
    fontSize: 13,
    fontWeight: '400',
    color: '#888280',
  },
  favStar: {
    fontSize: 15,
    color: '#E7B32B',
  },
  // Action button (Games tab) -- height/padding/radius fixed; bg+fg+label vary
  // by analysis_state (see ACTION_VARIANT).
  reviewBtn: {
    paddingHorizontal: 12,
    height: 30,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  reviewBtnText: {
    fontSize: 12,
    fontWeight: '600',
  },
  resultBadge: {
    width: 28,
    height: 28,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  resultBadgeText: {
    fontSize: 12,
    fontWeight: '700',
    color: '#fff',
  },
  opponentCell: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  // Fixed-width slot so opponent names line up whether the row carries a
  // platform glyph or none.
  platLogoSlot: {
    width: 22,
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
  gameOpponent: {
    fontSize: 13,
    fontWeight: '600',
    color: '#262322',
  },
  gameOpponentLinked: {
    color: '#3DAB9E',
    textDecorationLine: 'underline',
  },
  gameMeta: {
    fontSize: 11,
    color: '#888280',
    marginTop: 2,
    textAlign: 'center',
  },
  cellText: {
    fontSize: 12,
    color: '#4D3F37',
    textAlign: 'center',
  },
  cellMuted: {
    fontSize: 12,
    color: '#A8A7A6',
  },
  accuracyCell: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  accuracyLabel: {
    fontSize: 11,
    color: '#888280',
    width: 34,
    textAlign: 'right',
  },
  gameResult: {
    fontSize: 13,
    fontWeight: '700',
  },
  emptyText: {
    fontSize: 12,
    color: '#888280',
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 40,
    gap: 12,
  },
  emptyTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: '#262322',
  },
  pagerRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 16,
    marginTop: 12,
  },
  pagerButton: {
    paddingVertical: 6,
    paddingHorizontal: 14,
    borderRadius: 999,
    backgroundColor: '#FAF6EB',
  },
  pagerButtonDisabled: {
    opacity: 0.4,
  },
  pagerButtonText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#4D3F37',
  },
  pagerLabel: {
    fontSize: 12,
    color: '#888280',
  },
});

// Result badge is a teal/orange/grey tile with the result initial (design's
// Games table) -- NOT green/red, which is reserved for the rating delta.
const resultBadgeColor = (result: string | null) => {
  if (result === 'Win') {
    return '#3DAB9E';
  }
  if (result === 'Loss') {
    return '#E17846';
  }
  return '#FACF47';
};

// Rating delta text color follows the sign of the change (delta tokens).
const deltaColor = (delta: number | null) => {
  if (delta == null || delta === 0) {
    return '#888280';
  }
  return delta > 0 ? '#2F7F4C' : '#CD2C39';
};

// End-reason buckets from the backend are lowercase; title-case for display and
// hide the catch-all "other" bucket.
const endReasonLabel = (reason: string | null) => {
  if (!reason || reason === 'other') {
    return null;
  }
  return reason.charAt(0).toUpperCase() + reason.slice(1);
};

// Time controls are stored base-in-seconds ("Blitz 180+2"); the design shows
// minutes ("Blitz 3+2"). Convert the "<base>+<inc>" portion, guarding against
// values already in minutes (base < 60) and non-standard controls (e.g.
// correspondence "1/259200", which has no "s+i" match and is left untouched).
const displayFormat = (format: string | null) => {
  if (!format) {
    return '—';
  }
  return format.replace(/(\d+)\s*\+\s*(\d+)/, (whole, base, inc) => {
    const secs = Number(base);
    if (secs < 60) {
      return whole;
    }
    const mins = secs / 60;
    const minStr = Number.isInteger(mins) ? String(mins) : mins.toFixed(1);
    return `${minStr}+${inc}`;
  });
};

// Action button per analysis_state: gold "Review" (not analyzed -- kick off the
// engine review) / cream "View" (analyzed, just open it). Falls back to Review
// for a legacy payload that predates the analysis_state field.
const ACTION_VARIANT: Record<string, {label: string; bg: string; fg: string}> = {
  review: {label: 'Review', bg: '#FACF47', fg: '#4D3F37'},
  view: {label: 'View', bg: '#FAF0D1', fg: '#4D3F37'},
};

const timeAgo = (iso: string | null) => {
  if (!iso) {
    return '';
  }
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) {
    return '';
  }
  const days = Math.floor((Date.now() - then) / 86400000);
  if (days <= 0) {
    return 'Today';
  }
  if (days === 1) {
    return 'Yesterday';
  }
  return `${days} days ago`;
};

// Filter chips per the design's "8 filter chips" row -- limited here to the
// filters the backend's `get_player_games` service actually supports
// (result / time_class / platform query params). "Not analysed" and
// "Starred" from the design are deliberately omitted: `PlayerGameRow` has no
// `analyzed`/`is_favourite` field, so there's no real data to filter on.
const FILTERS: {id: string; label: string; params: {result?: string; time_class?: string; platform?: string}}[] = [
  {id: 'all', label: 'All games', params: {}},
  {id: 'wins', label: 'Wins', params: {result: 'Win'}},
  {id: 'losses', label: 'Losses', params: {result: 'Loss'}},
  {id: 'bullet', label: 'Bullet', params: {time_class: 'bullet'}},
  {id: 'blitz', label: 'Blitz', params: {time_class: 'blitz'}},
  {id: 'rapid', label: 'Rapid', params: {time_class: 'rapid'}},
  {id: 'lichess', label: 'Lichess', params: {platform: 'lichess'}},
  {id: 'chess_com', label: 'Chess.com', params: {platform: 'chess_com'}},
  {id: 'circlechess', label: 'CircleChess', params: {platform: 'circlechess'}},
];

type Props = {
  username?: string;
  navigation?: any;
  // Gates the review/view action -- a visitor can see the games list (this
  // tab is open to anyone) but only the owner can open a game's full
  // analysis; the backend enforces this too (see game/views.py's get_games
  // ownership check).
  isOwner?: boolean;
};

// Full paginated Games tab (Milestone 2) -- unlike Training/Assessment, this
// tab is open to any authenticated viewer (owner or visitor), so it's always
// fetched once the profile itself loaded successfully.
export default function GamesTab({username, navigation, isOwner = false}: Props) {
  const [page, setPage] = useState(1);
  const [filterId, setFilterId] = useState('all');
  const filterParams = FILTERS.find(f => f.id === filterId)?.params ?? {};
  const {data, isLoading, isError} = useGetPlayerGamesApi(username, page, filterParams);

  const onSelectFilter = (id: string) => {
    setFilterId(id);
    setPage(1);
  };

  return (
    <Box style={styles.card}>
      <View style={styles.filterRow}>
        {FILTERS.map(f => {
          const active = f.id === filterId;
          return (
            <Pressable
              key={f.id}
              testID={`games-filter-${f.id}`}
              onPress={() => onSelectFilter(f.id)}
              style={[styles.filterChip, active && styles.filterChipActive]}>
              <CCText
                style={[styles.filterChipText, active && styles.filterChipTextActive]}>
                {f.label}
              </CCText>
            </Pressable>
          );
        })}
      </View>

      {isLoading ? (
        <CCLoader loading={true} isComponentLoader />
      ) : isError || !data ? (
        <CCText style={styles.emptyText}>Couldn't load games.</CCText>
      ) : data.data.length ? (
        <>
          {/* The table's columns have real minWidths (see colOpponent etc.)
              adding up to well past a phone's viewport -- without this,
              narrower screens crushed every column down to near-zero,
              wrapping "Opponent" one letter per line and overlapping cell
              content. A horizontal scroll keeps every column at its natural
              width instead of squeezing anything. */}
          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          <View style={styles.tableMinWidth}>
          <View style={styles.headerRow}>
            <CCText style={[styles.headerCell, styles.colResult]}>Result</CCText>
            <CCText
              style={[styles.headerCell, styles.colOpponent]}
              numberOfLines={1}>
              Opponent
            </CCText>
            <CCText style={[styles.headerCell, styles.colFormat]}>Format</CCText>
            <CCText style={[styles.headerCell, styles.colMoves]}>Moves</CCText>
            <CCText style={[styles.headerCell, styles.colAccuracy]}>
              Accuracy
            </CCText>
            <CCText style={[styles.headerCell, styles.colOpening]}>Opening</CCText>
            <CCText style={[styles.headerCell, styles.colRating]}>Rating</CCText>
            <View style={styles.colActions} />
          </View>
          {data.data.map((row, idx) => {
            const platform = platformLogo(row.platform);
            const action =
              ACTION_VARIANT[row.analysis_state ?? 'review'] ??
              ACTION_VARIANT.review;
            const metaLine = [timeAgo(row.end_time), endReasonLabel(row.end_reason)]
              .filter(Boolean)
              .join(' · ');
            const content = (
              <>
                <View style={styles.colResult}>
                  <View
                    style={[
                      styles.resultBadge,
                      {backgroundColor: resultBadgeColor(row.result)},
                    ]}>
                    <CCText
                      style={[
                        styles.resultBadgeText,
                        // Draw badge is gold; use dark text for contrast.
                        row.result !== 'Win' && row.result !== 'Loss'
                          ? {color: '#4D3F37'}
                          : null,
                      ]}>
                      {(row.result ?? '?').slice(0, 1)}
                    </CCText>
                  </View>
                </View>
                <View style={[styles.colOpponent, styles.opponentCell]}>
                  <View style={styles.platLogoSlot}>
                    {platform ? (
                      <Image
                        source={platform.logo}
                        style={{width: PLAT_GLYPH, height: PLAT_GLYPH}}
                        resizeMode="contain"
                        accessibilityLabel={platform.name}
                      />
                    ) : null}
                  </View>
                  <View style={{flexShrink: 1, minWidth: 0, alignItems: 'center'}}>
                    <View style={styles.opponentNameRow}>
                      {/* opponent_cc_username is backend-resolved: set only for
                          games played ON CircleChess whose opponent exists as a
                          CCUser -- null for Chess.com/Lichess handles and Caissa,
                          which have no profile to open. */}
                      {row.opponent_cc_username ? (
                        <Pressable
                          testID="player-games-opponent-link"
                          // Independent of the row's own press (opens the game
                          // review) -- tapping the username opens their profile.
                          onPress={e => {
                            e?.stopPropagation?.();
                            openProfile(navigation, row.opponent_cc_username);
                          }}>
                          <CCText
                            style={[styles.gameOpponent, styles.gameOpponentLinked]}
                            numberOfLines={1}>
                            {row.opponent ?? 'Unknown'}
                          </CCText>
                        </Pressable>
                      ) : (
                        <CCText style={styles.gameOpponent} numberOfLines={1}>
                          {row.opponent ?? 'Unknown'}
                        </CCText>
                      )}
                      {row.opponent_rating != null ? (
                        <CCText style={styles.opponentRating}>
                          {row.opponent_rating}
                        </CCText>
                      ) : null}
                    </View>
                    {metaLine ? (
                      <CCText style={styles.gameMeta} numberOfLines={1}>
                        {metaLine}
                      </CCText>
                    ) : null}
                  </View>
                </View>
                <CCText
                  style={[styles.colFormat, styles.cellText]}
                  numberOfLines={1}>
                  {displayFormat(row.format)}
                </CCText>
                <CCText style={[styles.colMoves, styles.cellText]}>
                  {row.moves ?? '—'}
                </CCText>
                <View style={[styles.colAccuracy, styles.accuracyCell]}>
                  {row.accuracy != null ? (
                    <>
                      <View style={{flex: 1}}>
                        <AnimatedBar
                          percent={row.accuracy}
                          color={CCColors.BlueAqua}
                        />
                      </View>
                      <CCText style={styles.accuracyLabel}>
                        {Math.round(row.accuracy)}%
                      </CCText>
                    </>
                  ) : (
                    <CCText style={styles.cellMuted}>—</CCText>
                  )}
                </View>
                <CCText
                  style={[styles.colOpening, styles.cellText]}
                  numberOfLines={1}>
                  {row.opening ?? '—'}
                </CCText>
                <CCText
                  style={[
                    styles.colRating,
                    styles.gameResult,
                    {color: deltaColor(row.rating_delta)},
                  ]}>
                  {row.rating_delta != null
                    ? `${row.rating_delta > 0 ? '+' : ''}${row.rating_delta}`
                    : '—'}
                </CCText>
                <View style={[styles.colActions, styles.actionsCell]}>
                  {row.is_favourite ? (
                    <CCText style={styles.favStar}>★</CCText>
                  ) : null}
                  {row.uuid && isOwner ? (
                    <Pressable
                      testID="player-games-review"
                      style={[styles.reviewBtn, {backgroundColor: action.bg}]}
                      onPress={e => {
                        // Stop the row's own press (opponent navigation) from
                        // also firing on CircleChess-opponent rows.
                        e?.stopPropagation?.();
                        window.location.href = `${window.location.origin}/chessAnalysis?game_id=${row.uuid}`;
                      }}>
                      <CCText style={[styles.reviewBtnText, {color: action.fg}]}>
                        {action.label}
                      </CCText>
                    </Pressable>
                  ) : null}
                </View>
              </>
            );

            // The row opens this game's review -- owner-only (see comment
            // on `isOwner` in Props above). The opponent name (above)
            // independently opens their profile via its own Pressable,
            // regardless of owner-ness. Plain browser navigation (not
            // navigation.navigate) -- same window.location.origin-based URL
            // building the share button already uses.
            if (row.uuid && isOwner) {
              return (
                <Pressable
                  key={row.uuid}
                  testID="player-games-row"
                  style={styles.gameRow}
                  onPress={() => {
                    window.location.href = `${window.location.origin}/chessAnalysis?game_id=${row.uuid}`;
                  }}>
                  {content}
                </Pressable>
              );
            }

            return (
              <View key={row.uuid ?? idx} testID="player-games-row" style={styles.gameRow}>
                {content}
              </View>
            );
          })}
          </View>
          </ScrollView>

          {data.total_pages > 1 ? (
            <View style={styles.pagerRow}>
              <Pressable
                testID="games-pager-prev"
                disabled={page <= 1}
                style={[styles.pagerButton, page <= 1 && styles.pagerButtonDisabled]}
                onPress={() => setPage(p => Math.max(1, p - 1))}>
                <CCText style={styles.pagerButtonText}>Prev</CCText>
              </Pressable>
              <CCText style={styles.pagerLabel}>
                Page {data.current_page} of {data.total_pages}
              </CCText>
              <Pressable
                testID="games-pager-next"
                disabled={page >= data.total_pages}
                style={[
                  styles.pagerButton,
                  page >= data.total_pages && styles.pagerButtonDisabled,
                ]}
                onPress={() => setPage(p => Math.min(data.total_pages, p + 1))}>
                <CCText style={styles.pagerButtonText}>Next</CCText>
              </Pressable>
            </View>
          ) : null}
        </>
      ) : (
        <View style={styles.emptyState}>
          <ProfileAsset name="caissaGirl" size={82} />
          <CCText style={styles.emptyTitle}>No games played yet</CCText>
        </View>
      )}
    </Box>
  );
}
